import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SN_MTL_IRQ_REG_003_POP_001 from "@menu/MTL/SN_MTL_IRQ_REG_003/SN_MTL_IRQ_REG_003_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_IRQ_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo, config } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsD = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_irq_reg_003_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_req: nodeData.no_req,
        dt_from: nodeData.dt_req,
        dt_to: nodeData.dt_req,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    let hasInvalid = false;
    // 안보이는 isureql항목 삭제
    const nodesToDelete: any[] = [];
    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      if (node.data.no_req === focusedRowNodeH.data.no_req) {
        if (node.data.qt_io !== 0 || node.data.qt_req_rev !== 0) {
          hasInvalid = true;
        }
        nodesToDelete.push(node);
      }
    });

    if (hasInvalid) {
      showDialog("alter", "이미 진행된 건이 있어 삭제할 수 없습니다.");
      return;
    }

    nodesToDelete.forEach((node) => {
      getGridCurrent("grid3").deleteRowByNode(node);
    });

    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeD = getGridCurrent("grid2").focusedRowNode();
    let hasInvalid = false;
    // 행삭제시 안보이는 isureql 수량 변경
    if (focusedRowNodeD.data.state !== "insert") {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node.data.no_req === focusedRowNodeD.data.no_req &&
          node.data.cd_item === focusedRowNodeD.data.cd_matl
        ) {
          if (node.data.qt_io !== 0 || node.data.qt_req_rev !== 0) {
            hasInvalid = true;
            return;
          }
          const qt = node.data.qt - focusedRowNodeD.data.qt_req;
          node.setDataValue("qt_good", qt, "noEvent");
          node.setDataValue("qt", qt, "noEvent");
        }
      });
    }
    if (hasInvalid) {
      showDialog("alter", "이미 진행된 건입니다.");
      return;
    }

    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // D 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const isureqhChanges = getGridCurrent("grid1").getChange();
      const isureqdChanges = getGridCurrent("grid2").getChange();
      const headerInserts = isureqdChanges.insert ?? [];
      const detaiInserts = isureqdChanges.insert ?? [];
      const detailDeltes = isureqdChanges.delete ?? [];
      if (headerInserts.length > 0 && detaiInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }
      if (
        isureqhChanges.modifiedRowcount === 0 &&
        isureqdChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 안보이는 isureql 생성
      const isureqlIChanges = getGridCurrent("grid3").getChange();
      const lineInserts = isureqlIChanges.insert ?? []; // 저장실패시 중복으로 안 돌게
      if (detaiInserts.length > 0 && lineInserts.length < 1) {
        // 수정중인 행 포커스 잡아주기
        let focusedRowHIdx = 0;
        const no_req = detaiInserts[0].no_req;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.no_req === no_req) {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

        const itemMap = new Map<string, any>();

        detaiInserts.forEach((item: any) => {
          // 1. 복합키 생성 (cd_item + no_req)
          const compositeKey = `${item.no_req}|${item.cd_matl}|${converter.getString(item.cd_pjt)}`;

          if (itemMap.has(compositeKey)) {
            const existingItem = itemMap.get(compositeKey);
            // 수량 합산
            existingItem.qt_req += Number(item.qt_req) || 0;
          } else {
            // 신규 등록
            itemMap.set(compositeKey, { ...item });
          }
        });

        itemMap.forEach((groupedItem) => {
          // grid3에 빈 행 추가
          const addedNode = getGridCurrent("grid3").addRow();
          const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");

          if (addedNode) {
            addedNode.setDataValue(
              "cd_firm",
              focusedRowNodeH.data?.cd_firm,
              "noEvent"
            );
            addedNode.setDataValue("no_req", groupedItem.no_req, "noEvent");
            addedNode.setDataValue("no_line", maxNoLine + 1, "noEvent");
            addedNode.setDataValue("cd_item", groupedItem.cd_matl, "noEvent");
            addedNode.setDataValue("qt_good", groupedItem.qt_req, "noEvent");
            addedNode.setDataValue("qt", groupedItem.qt_req, "noEvent");
            addedNode.setDataValue("qt_io", 0, "noEvent");
            addedNode.setDataValue("qt_req_rev", 0, "noEvent");
            addedNode.setDataValue(
              "cd_wh",
              focusedRowNodeH.data?.cd_wh,
              "noEvent"
            );
            addedNode.setDataValue(
              "st_req",
              focusedRowNodeH.data.st_req,
              "noEvent"
            );
            addedNode.setDataValue("cd_pjt", groupedItem.cd_pjt, "noEvent");
            addedNode.setDataValue("yn_req", "Y", "noEvent");
          }
        });
      }

      // 안보이는 isureql 수량0인건 삭제
      if (detailDeltes.length > 0) {
        const nodesToDelete: any[] = [];
        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (node.data.qt <= 0) {
            nodesToDelete.push(node);
          }
        });

        nodesToDelete.forEach((node) => {
          getGridCurrent("grid3").deleteRowByNode(node);
        });
      }
      const isureqlChanges = getGridCurrent("grid3").getChange();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_req",
        cd_module: "MTL",
        no_line: 11,
        dt: focusedRowNodeH.data.dt_req,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid3"],
        ],
        inserts: [isureqhChanges, isureqlChanges, isureqdChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        isureqh: isureqhChanges,
        isureql: isureqlChanges,
        isureql2: isureqdChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_IRQ_REG_003_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const mtl0015 = config?.MTL0015?.dc_ctrl ?? "";

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        no_req: "",
        dt_req: today,
        cd_io: "top1",
        fg_slip: "2",
        tp_slip: "5",
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        st_req: mtl0015 === "Y" ? "P" : "F",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io);
        rowNode.setDataValue("fg_money", ioType.fg_money);
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_req = selectedRow.no_req;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_req);
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") {
        focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
      }
    });

    getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

    switch (id) {
      case "btn_POP1":
        {
          // searchPanel에서 선택된 값 전달
          const selectedFormData = searchRef.current.getFormdata();
          const selectedData = {
            cd_biz: selectedFormData.cd_biz,
            dt_from: selectedFormData.dt_from,
            dt_to: selectedFormData.dt_to,
          };

          const component = (
            <SN_MTL_IRQ_REG_003_POP_001 selectedData={selectedData} />
          );
          const result = await showResizablePopupModal({
            title: "작업지시적용",
            children: component,
          });

          if (!result || result === "close") return;

          const headerResult = result[0];
          const lineResult = result[1];

          // 요청도움창 Header 결과 적용
          // 요청도움창 Line 결과 적용
          lineResult.map((result: any) => {
            const header = headerResult.filter(
              (header: any) => header.no_wo === result.no_wo
            );
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                switch (key) {
                  case "no_line":
                    addedNode.setDataValue(
                      "no_line_wo",
                      result[key],
                      "noEvent"
                    );
                    break;
                  default:
                    addedNode.setDataValue(key, result[key], "noEvent");
                    break;
                }
              }
              addedNode.setDataValue(
                "qt_req",
                result.qt_need - result.qt_req,
                "noEvent"
              );
              addedNode.setDataValue("cd_pjt", header[0].cd_pjt, "noEvent");
              addedNode.setDataValue("nm_pjt", header[0].nm_pjt, "noEvent");
              getGridCurrent("grid2").applyBlock(addedNode);
            }
          });
        }
        break;
    }
  };
  //#endregion

  //#region GridD Events
  const onBeforeAddRowD = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      // 저장시 line을 강제로 만들어주는거라 한 번에 한 요청씩만 저장할 수 있게(데이터 꼬일 위험 있음)
      const modifiedRowsD = getGridCurrent("grid2").getChange().insert || [];
      if (
        modifiedRowsD.length > 0 &&
        getGridCurrent("grid2").getDisplayedRowCount() > 0
      ) {
        const focusedRowNodeD = getGridCurrent("grid2").focusedRowNode();
        if (focusedRowNodeD) {
          if (
            modifiedRowsD[0].no_req !== focusedRowNodeD.data.no_req &&
            focusedRowNodeD.data.state === "insert"
          ) {
            showDialog("warning", "다른 요청번호에 수정된 내용이 있습니다.");
            return false;
          }
        }
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        no_req: focusedNodeH.data?.no_req,
        no_line: maxNoLine + 1,
        qt_req: 0,
        no_wo: "",
        no_line_wo: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowD,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "요청정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["no_req"],
            }}
            header={
              <div>
                <SnButton
                  text={"작업지시적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
          <div className="hidden">
            <SnGrid
              ref={gridRefs.current["grid3"]} // Grid Name
              columnConfig={grids.grid3.columns} // 컬럼 설정 전달
              rowData={grids.grid3.dataSource} // Datasource
            />
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_IRQ_REG_003;
